import React, { ReactNode, SyntheticEvent } from "react";
import { connect } from "react-redux";

import { camelCase } from "lodash-es";
import { defineMessage, defineMessages, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { stopEventPropagation } from "@mapmycustomers/shared/util/browser";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";

import ButtonLink from "@app/component/ButtonLink";
import IntervalUnit, { getIntervalUnitDisplayName } from "@app/enum/IntervalUnit";
import MmcNotificationAction from "@app/enum/MmcNotificationAction";
import MmcNotificationCategory from "@app/enum/MmcNotificationCategory";
import Path from "@app/enum/Path";
import { getCurrentUserId } from "@app/store/iam";
import { getAllUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import MmcNotification, { MmcNotificationEntity } from "@app/types/MmcNotification";
import { MINUTES_IN_DAY, MINUTES_IN_HOUR, MINUTES_IN_WEEK } from "@app/util/consts";
import useSourceObjectMessageArgs from "@app/util/hook/useSourceObjectMessageArgs";
import { getEntityTypeDisplayName, getFeatureDisplayName } from "@app/util/ui";

import styles from "../NotificationCommon.module.scss";

const descriptionMessages = defineMessages({
  [MmcNotificationAction.ACTIVITY_ASSIGNED]: {
    id: "navbar.notifications.list.notification.description.activityAssigned",
    defaultMessage:
      "{user} - has assigned an activity to you{associatedEntityType, select, accounts { with <b>{associatedEntityName}</b>} contacts { with <b>{associatedEntityName}</b>} deals { relating to <b>{associatedEntityName}</b>} other {}}",
    description: "Navbar Notifications - list - notification - activity assigned description",
  },
  [MmcNotificationAction.ACTIVITY_LOGGED]: {
    id: "navbar.notifications.list.notification.description.activityLogged",
    defaultMessage: "{user} logged an activity with {associatedEntityLink}",
    description: "Navbar Notifications - list - notification - activity logged description",
  },
  [MmcNotificationAction.ACTIVITY_NOTE_ADDED]: {
    id: "navbar.notifications.list.notification.description.activity.noteAdded",
    defaultMessage: "{user} added a note on {entityLink}",
    description: "Navbar Notifications - list - notification - activity note added description",
  },
  [MmcNotificationAction.ACTIVITY_NOTE_UPDATED]: {
    id: "navbar.notifications.list.notification.description.activity.noteUpdated",
    defaultMessage: "{user} updated the note on {entityLink}",
    description: "Navbar Notifications - list - notification - activity note updated description",
  },
  [MmcNotificationAction.ACTIVITY_STARTING]: {
    id: "navbar.notifications.list.notification.description.activityStarting",
    defaultMessage: "Activity starting in {remindBefore} {intervalUnit}",
    description: "Navbar Notifications - list - notification - activity starting",
  },
  [MmcNotificationAction.ACTIVITY_TIME_CHANGED]: {
    id: "navbar.notifications.list.notification.description.activityTimeChanged",
    defaultMessage: "{user} updated the time of an activity",
    description: "Navbar Notifications - list - notification - activity time changed",
  },
  [MmcNotificationAction.AUTO_TRACKED_TRIPS]: {
    id: "navbar.notifications.list.notification.description.autoTrackedTrips",
    defaultMessage:
      "{count, plural, =0 {No drives} one {1 drive} other {{count} drives}} tracked this week",
    description: "Navbar Notifications - list - notification - auto tracked trips",
  },
  [MmcNotificationAction.BOUNCED_EMAIL]: {
    id: "navbar.notifications.list.notification.description.bouncedEmail",
    defaultMessage: "We couldn't deliver an email to {entityLink}",
    description: "Navbar Notifications - list - notification - bounced email",
  },
  [MmcNotificationAction.COACHING_NOTE_CREATED]: {
    id: "navbar.notifications.list.notification.description.coaching.noteCreated",
    defaultMessage:
      "{user} wrote a coaching note{coachingNoteForOther, select, true { for {noteCreatedForSelf, select, true {themselves} other {<b>{subjectUserName}</b>}}} other { for you}}",
    description: "Navbar Notifications - list - notification - coaching note created",
  },
  [MmcNotificationAction.COACHING_NOTE_REPLIED]: {
    id: "navbar.notifications.list.notification.description.coaching.noteReplied",
    defaultMessage: "{user} replied to a coaching note",
    description: "Navbar Notifications - list - notification - coaching note replied",
  },
  [MmcNotificationAction.COACHING_TASK_ASSIGNED]: {
    id: "navbar.notifications.list.notification.description.coaching.taskAssigned",
    defaultMessage:
      "{user} assigned {taskAssignedToOther, select, true {<b>{subjectUserName}</b>} other {you}} a task",
    description: "Navbar Notifications - list - notification - coaching task assigned",
  },
  [MmcNotificationAction.COACHING_TASK_COMPLETED]: {
    id: "navbar.notifications.list.notification.description.coaching.taskCompleted",
    defaultMessage:
      "{user} completed {taskCompletedForSelf, select, true {their} other {{taskCompletedForOther, select, true {<b>{subjectUserName}</b>'s} other {your}}}} task",
    description: "Navbar Notifications - list - notification - coaching task completed",
  },
  [MmcNotificationAction.COACHING_TASK_CREATED]: {
    id: "navbar.notifications.list.notification.description.coaching.taskCreated",
    defaultMessage:
      "{user} created a task for {taskCreatedForSelf, select, true {themselves} other {<b>{subjectUserName}</b>}}",
    description: "Navbar Notifications - list - notification - coaching task created",
  },
  [MmcNotificationAction.COACHING_TASK_MENTIONED]: {
    id: "navbar.notifications.list.notification.description.coaching.taskMentioned",
    defaultMessage: "{user} mentioned you in a task",
    description: "Navbar Notifications - list - notification - coaching task mentioned",
  },
  [MmcNotificationAction.COACHING_TASK_OVERDUE]: {
    id: "navbar.notifications.list.notification.description.coaching.taskOverdue",
    defaultMessage: "<b>{subjectUserName}</b>'s task is overdue",
    description: "Navbar Notifications - list - notification - coaching task overdue",
  },
  [MmcNotificationAction.COMPANY_GROUP_SHARED]: {
    id: "navbar.notifications.list.notification.description.companyGroupShared",
    defaultMessage: "1 Company group was shared with you",
    description: "Navbar Notifications - list - notification - company group shared description",
  },
  [MmcNotificationAction.COMPANY_ROUTE_SHARED]: {
    id: "navbar.notifications.list.notification.description.companyRouteShared",
    defaultMessage: "1 Company route was shared with you",
    description: "Navbar Notifications - list - notification - company route shared description",
  },
  [MmcNotificationAction.DEAL_GROUP_SHARED]: {
    id: "navbar.notifications.list.notification.description.dealGroupShared",
    defaultMessage: "1 deal group was shared with you",
    description: "Navbar Notifications - list - notification - deal group shared description",
  },
  [MmcNotificationAction.DEALS_MARKED_LOST]: {
    id: "navbar.notifications.list.notification.description.dealsMarkedLost",
    defaultMessage:
      "{count, plural, one {A Deal has} other {{count} Deals have}} been marked Closed Lost",
    description: "Navbar Notifications - list - notification - deals marked lost description",
  },
  [MmcNotificationAction.DEALS_MARKED_WON]: {
    id: "navbar.notifications.list.notification.description.dealsMarkedWon",
    defaultMessage:
      "{count, plural, one {A Deal has} other {{count} Deals have}} been marked Closed Won",
    description: "Navbar Notifications - list - notification - deals marked won description",
  },
  [MmcNotificationAction.DEALS_ROTTING]: {
    id: "navbar.notifications.list.notification.description.dealsRotting",
    defaultMessage: "{count, plural, one {A Deal} other {{count} Deals}} started rotting",
    description: "Navbar Notifications - list - notification - deals rotting description",
  },
  [MmcNotificationAction.DEALS_STAGE_CHANGED]: {
    id: "navbar.notifications.list.notification.description.dealsStageChanged",
    defaultMessage:
      "{count, plural, one {A Deal has} other {{count} Deals have}} been moved to a new stage",
    description: "Navbar Notifications - list - notification - deals stage changed description",
  },
  [MmcNotificationAction.EMAIL_TEMPLATE_SHARED]: {
    id: "navbar.notifications.list.notification.description.emailTemplateShared",
    defaultMessage: "{user} shared an email template with you: <b>{entityName}</b>",
    description: "Navbar Notifications - list - notification - template shared description",
  },
  [MmcNotificationAction.FEATURE_REQUESTED]: {
    id: "navbar.notifications.list.notification.description.featureUpgradeRequest",
    defaultMessage:
      "{user} expressed interest in {featureDisplayName, select, none {a feature} other {the {featureDisplayName} feature}}",
    description: "Navbar Notifications - list - notification - feature upgrade request",
  },
  [MmcNotificationAction.FOLDER_SHARED]: {
    id: "navbar.notifications.list.notification.description.folderShared",
    defaultMessage: "{user} shared a resource folder with you: {entityLink}",
    description: "Navbar Notifications - list - notification - folder shared description",
  },
  [MmcNotificationAction.GUIDE_ASSIGNED]: {
    id: "navbar.notifications.list.notification.description.guideAssigned",
    defaultMessage:
      "{isDefaultGuideAssignment, select, true {{guideFlowId, select, mmcGettingStarted {Check out the {guideName} guide to learn how to easily navigate our web app.} mmcGettingStartedMobile {Check out the {guideName} guide to learn how to easily navigate our mobile app.} mmcMapsOnboarding {Check out the {guideName} guide to learn how to easily navigate the map.} other {Check out the guide} }} other {{user} assigned {count} {count, plural, one {training guide} other {training guides}} to you}}",
    description: "Navbar Notifications - list - notification - shared deals closing",
  },
  [MmcNotificationAction.GUIDE_REMINDER]: {
    id: "navbar.notifications.list.notification.description.guideReminder",
    defaultMessage:
      "You have {count} more {count, plural, one {training guide} other {training guides}} to complete",
    description: "Navbar Notifications - list - notification - shared deals closing",
  },
  [MmcNotificationAction.INCOMPLETE_ACTIVITY]: {
    id: "navbar.notifications.list.notification.description.incomplete_activity",
    defaultMessage: "Did you finish “{entityName}”?",
    description: "Navbar Notifications - list - notification - incomplete activity",
  },
  [MmcNotificationAction.MARK_ACTIVITY_AS_DONE]: {
    id: "navbar.notifications.list.notification.description.mark_activity_as_done",
    defaultMessage: "Did you finish “{entityName}”?",
    description: "Navbar Notifications - list - notification - mark activity as done description",
  },
  [MmcNotificationAction.MENTION]: {
    id: "navbar.notifications.list.notification.description.mention",
    defaultMessage:
      "{user} {isTeamMention, select, true {mentioned your team} other {mentioned you}} in a note on {entityLink}",
    description: "Navbar Notifications - list - notification - mention description",
  },
  [MmcNotificationAction.NOTE_ADDED]: {
    id: "navbar.notifications.list.notification.description.noteAdded",
    defaultMessage: "{user} left a note on {entityLink}",
    description: "Navbar Notifications - list - notification - note added description",
  },
  [MmcNotificationAction.OUT_OF_CADENCE]: {
    id: "navbar.notifications.list.notification.description.outOfCadence",
    defaultMessage:
      "{count, plural, one {# record owned or shared with you is now past due} other {# records owned or shared with you are now past due}}",
    description: "Navbar Notifications - list - notification - out of cadence description",
  },
  [MmcNotificationAction.OVERDUE_ACTIVITIES]: {
    id: "navbar.notifications.list.notification.description.overdueActivities",
    defaultMessage:
      "{count, plural, one {An activity is} other {{count} activities became}} overdue",
    description: "Navbar Notifications - list - notification - overdue activities description",
  },
  [MmcNotificationAction.OWNED_DEALS_CLOSING]: {
    id: "navbar.notifications.list.notification.description.ownedDealsClosing",
    defaultMessage:
      "{count, plural, one {Set} other {Multiple deals set}} to close in {entityInterval, select, day {one day} week {7 days} other {30 days}}",
    description: "Navbar Notifications - list - notification - owned deals closing",
  },
  [MmcNotificationAction.OWNERSHIP_CHANGED]: {
    id: "navbar.notifications.list.notification.description.ownershipChanged",
    defaultMessage:
      "You’ve been made owner of {sourceObject, select, standard {a {entityType}} leadCompanies {a Lead} siteCompanies {a Site} projectCompanies {a Project} shopCompanies {a Shop} partnerPeople {a Partner} leadPeople {a Lead} staffMemberPeople {a Staff Member} leadPersonnel {a Lead Personnel} other {a record ({label})}}",
    description:
      'Navbar Notifications - list - notification - ownership changed description. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
  [MmcNotificationAction.PEOPLE_GROUP_SHARED]: {
    id: "navbar.notifications.list.notification.description.peopleGroupShared",
    defaultMessage: "1 people group was shared with you",
    description: "Navbar Notifications - list - notification - people group shared description",
  },
  [MmcNotificationAction.PEOPLE_ROUTE_SHARED]: {
    id: "navbar.notifications.list.notification.description.peopleRouteShared",
    defaultMessage: "1 people route was shared with you",
    description: "Navbar Notifications - list - notification - people route shared description",
  },
  [MmcNotificationAction.RESOURCE_SHARED]: {
    id: "navbar.notifications.list.notification.description.resourceShared",
    defaultMessage: "{user} shared a resource with you: {entityLink}",
    description: "Navbar Notifications - list - notification - resource shared description",
  },
  [MmcNotificationAction.SAVED_FILTER_SHARED]: {
    id: "navbar.notifications.list.notification.description.savedFilterShared",
    defaultMessage: "{user} shared a saved filter with you",
    description: "Navbar Notifications - list - notification - savedFilter shared description",
  },
  [MmcNotificationAction.SHARED_DEALS_CLOSING]: {
    id: "navbar.notifications.list.notification.description.sharedDealsClosing",
    defaultMessage:
      "{count, plural, one {Set} other {Multiple deals set}} to close in {entityInterval, select, day {one day} week {7 days} other {30 days}}",
    description: "Navbar Notifications - list - notification - shared deals closing",
  },
  [MmcNotificationAction.TRIP_FLAGGED]: {
    id: "navbar.notifications.list.notification.description.tripFlagged",
    defaultMessage: "{user} flagged your drive from",
    description: "Navbar Notifications - list - notification - unclassified trips",
  },
  [MmcNotificationAction.UNCLASSIFIED_TRIPS]: {
    id: "navbar.notifications.list.notification.description.unclassifiedTrips",
    defaultMessage:
      "{count} {count, plural, one {drive needs} other {drives need}} to be classified",
    description: "Navbar Notifications - list - notification - unclassified trips",
  },
  [MmcNotificationAction.UNIFIED_ROUTE_SHARED]: {
    id: "navbar.notifications.list.notification.description.unifiedRouteShared",
    defaultMessage: "1 route was shared with you",
    description: "Navbar Notifications - list - notification - unified route shared description",
  },
});

const coachingMentionMessage = defineMessage({
  id: "navbar.notifications.list.notification.description.coaching.mention",
  defaultMessage: "{user} mentioned you in a coaching note",
  description: "Navbar Notifications - list - notification - coaching note mention description",
});

interface DescriptionProps {
  notification: MmcNotification;
  onEntityClick: (entity: MmcNotificationEntity, event?: SyntheticEvent<HTMLElement>) => void;
}

const Description = ({
  currentUserId,
  notification: {
    action,
    associatedEntity,
    category,
    count,
    directMentionUserIds,
    entity,
    featureKey,
    guides,
    isDefaultGuideAssignment,
    subjectUserId,
    updatedBy,
    url,
  },
  onEntityClick,
  users,
}: DescriptionProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  // Only ownership notifications name the type; the server may not send sourceObject yet
  const sourceObjectArgs = useSourceObjectMessageArgs(entity?.[0]?.type, entity?.[0]?.sourceObject);

  let intervalUnit;
  let remindBefore = entity?.[0]?.remindBeforeMinutes;

  if (remindBefore) {
    if (remindBefore % MINUTES_IN_WEEK === 0) {
      remindBefore = Math.round(remindBefore / MINUTES_IN_WEEK);
      intervalUnit = getIntervalUnitDisplayName(intl, IntervalUnit.WEEK, remindBefore);
    } else if (remindBefore % MINUTES_IN_DAY === 0) {
      remindBefore = Math.round(remindBefore / MINUTES_IN_DAY);
      intervalUnit = getIntervalUnitDisplayName(intl, IntervalUnit.DAY, remindBefore);
    } else if (remindBefore % MINUTES_IN_HOUR === 0) {
      remindBefore = Math.round(remindBefore / MINUTES_IN_HOUR);
      intervalUnit = getIntervalUnitDisplayName(intl, IntervalUnit.HOUR, remindBefore);
    } else {
      intervalUnit = getIntervalUnitDisplayName(intl, IntervalUnit.MINUTE, remindBefore);
    }
  }

  const isGuideNotification = [
    MmcNotificationAction.GUIDE_ASSIGNED,
    MmcNotificationAction.GUIDE_REMINDER,
  ].includes(action);

  const isTripNotifications = [
    MmcNotificationAction.AUTO_TRACKED_TRIPS,
    MmcNotificationAction.UNCLASSIFIED_TRIPS,
  ].includes(action);

  const isCoachingMention =
    action === MmcNotificationAction.MENTION && category === MmcNotificationCategory.COACHING_NOTE;

  // Task mentions arrive as MENTION + COACHING_TASK category, not the dedicated
  // COACHING_TASK_MENTIONED action — route them to the task-mention message.
  const isCoachingTaskMention =
    action === MmcNotificationAction.MENTION && category === MmcNotificationCategory.COACHING_TASK;

  const subjectUser =
    subjectUserId != null ? users.find(({ id }) => id === subjectUserId) : undefined;

  const subjectIsOther = subjectUserId != null && subjectUserId !== currentUserId;

  const subjectIsActor = subjectUserId == null || subjectUserId === updatedBy?.id;

  return (
    <div className={styles.description}>
      {intl.formatMessage(
        isCoachingMention
          ? coachingMentionMessage
          : isCoachingTaskMention
            ? descriptionMessages[MmcNotificationAction.COACHING_TASK_MENTIONED]
            : descriptionMessages[action],
        {
          associatedEntityLink: associatedEntity ? (
            <ButtonLink onClick={(event) => onEntityClick(associatedEntity, event)}>
              {associatedEntity.name}
            </ButtonLink>
          ) : (
            ""
          ),
          associatedEntityName: associatedEntity?.name,
          associatedEntityType: associatedEntity?.type,
          b: (text: ReactNode) => <b>{text}</b>,
          coachingNoteForOther: subjectIsOther ? "true" : "false",
          count: isTripNotifications
            ? (count ?? 0)
            : isGuideNotification
              ? (guides?.length ?? 0)
              : (entity?.length ?? 0),
          entityInterval: entity?.[0]?.interval ?? "",
          entityLink:
            entity && entity.length > 0 ? (
              <ButtonLink
                href={
                  [EntityType.FOLDER, EntityType.RESOURCE].includes(entity[0].type)
                    ? (url ??
                      `${Path.RESOURCES}/${
                        entity[0].type === EntityType.RESOURCE ? "files" : "folders"
                      }/${entity[0].id}`)
                    : undefined
                }
                onClick={(event) => {
                  stopEventPropagation(event);
                  if (![EntityType.FOLDER, EntityType.RESOURCE].includes(entity[0].type)) {
                    onEntityClick(entity[0], event);
                  }
                }}
                target={url ? "_blank" : undefined}
              >
                {entity[0].name}
              </ButtonLink>
            ) : (
              ""
            ),
          entityName: entity && entity.length > 0 ? entity[0].name : "",
          entityType:
            entity && entity.length > 0
              ? getEntityTypeDisplayName(intl, entity[0].type, {
                  lowercase: false,
                  plural: false,
                })
              : "",
          featureDisplayName: featureKey ? getFeatureDisplayName(intl, featureKey) : "none",
          guideFlowId: camelCase(
            isDefaultGuideAssignment && guides?.length ? guides[0].frigadeFlowId : ""
          ),
          guideName: isDefaultGuideAssignment && guides?.length ? guides[0].displayName : "",
          intervalUnit,
          isDefaultGuideAssignment,
          isTeamMention: directMentionUserIds && !directMentionUserIds.includes(currentUserId),
          label: sourceObjectArgs.label,
          noteCreatedForSelf: subjectIsActor ? "true" : "false",
          remindBefore,
          sourceObject: sourceObjectArgs.sourceObject,
          subjectUserName: subjectUser ? userDisplayName(subjectUser) : "",
          taskAssignedToOther: subjectIsOther ? "true" : "false",
          taskCompletedForOther: subjectIsOther ? "true" : "false",
          taskCompletedForSelf: subjectIsActor ? "true" : "false",
          taskCreatedForSelf: subjectIsActor ? "true" : "false",
          user: updatedBy ? userDisplayName(updatedBy) : "",
        }
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  users: getAllUsersOfEntireOrganization(state),
});

export default connect(mapStateToProps)(Description);
